import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ChevronLeft, Upload } from 'lucide-react'

/**
 * Datei wählen als richtiger Knopf. Das nackte `<input type="file">` sieht in
 * jedem Browser anders aus und ist am Handy ein winziges „Choose File“ — hier
 * ist es ein Knopf in der Größe der anderen, der sagt, was er tut.
 */
export function FileButton({
  label,
  accept,
  disabled,
  multiple,
  onBegin,
  onFile,
  hint,
}: {
  label: string
  accept?: string
  disabled?: boolean
  /** Mehrere Dateien auf einmal — sie gehen nacheinander durch `onFile`. */
  multiple?: boolean
  /** Vor der ersten Datei einer Auswahl, etwa um alte Fehlermeldungen zu löschen. */
  onBegin?: () => void
  onFile: (file: File) => void | Promise<void>
  hint?: string
}) {
  const ref = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState<{ n: number; von: number } | null>(null)
  const [last, setLast] = useState<string | null>(null)
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <button
        type="button"
        className="btn-secondary w-full sm:w-auto"
        disabled={disabled || busy !== null}
        onClick={() => ref.current?.click()}
      >
        <Upload size={16} aria-hidden="true" />
        {busy ? (busy.von > 1 ? `Lädt hoch … ${busy.n}/${busy.von}` : 'Lädt hoch …') : label}
      </button>
      <input
        ref={ref}
        type="file"
        accept={accept}
        multiple={multiple}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? [])
          e.target.value = ''
          if (files.length === 0) return
          onBegin?.()
          void nacheinander(files, onFile, setBusy).then((zuletzt) => {
            setLast(zuletzt)
            setBusy(null)
          })
        }}
      />
      {(last || hint) && (
        <span className="truncate text-xs text-text-muted">{last ? `Zuletzt: ${last}` : hint}</span>
      )}
    </div>
  )
}

/**
 * Reicht die gewählten Dateien einzeln weiter — nacheinander, nicht alle
 * zugleich: Jede wird erst verkleinert (Bilder) und dann hochgeladen, und
 * zwanzig Fotos parallel hielten zwanzig dekodierte Großbilder zugleich im
 * Speicher des Handys. Ein Fehler bei einer hält die übrigen nicht auf; der
 * Aufrufer meldet ihn selbst. Gibt die Zeile für „Zuletzt: …“ zurück.
 */
export async function nacheinander(
  files: File[],
  onFile: (file: File) => void | Promise<void>,
  fortschritt: (stand: { n: number; von: number }) => void,
): Promise<string> {
  for (const [i, file] of files.entries()) {
    fortschritt({ n: i + 1, von: files.length })
    try {
      await onFile(file)
    } catch {
      // siehe oben
    }
  }
  return files.length === 1 ? files[0]!.name : `${files.length} Dateien`
}

/** Zurück zur Liste — nur am Handy, wo Liste und Bearbeiten nicht nebeneinander passen. */
export function BackButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="-ml-2 flex min-h-[2.75rem] items-center gap-1 rounded-full pr-3 text-[0.95rem] font-medium text-brand lg:hidden"
    >
      <ChevronLeft size={22} aria-hidden="true" />
      {label}
    </button>
  )
}

/**
 * Überblendung von unten am Handy, mittig ab md: für Inhalte, die man
 * liest und wieder schließt (ein Gesprächsverlauf). Kein Fokus-Falle-Paket —
 * Escape und der Hintergrund schließen, der Schließen-Knopf steht oben.
 */
export function Sheet({
  title,
  onClose,
  children,
}: {
  title: string
  onClose: () => void
  children: ReactNode
}) {
  // Der Hintergrund scrollt sonst am Handy mit, wenn man im Verlauf wischt.
  useEffect(() => {
    const vorher = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = vorher
    }
  }, [])
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/50 md:items-center md:p-6"
      onClick={onClose}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div
        className="flex max-h-[88vh] w-full flex-col rounded-t-3xl bg-bg-elevated shadow-[var(--shadow-card)] md:max-w-2xl md:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
          <h3 className="truncate text-base">{title}</h3>
          <button
            type="button"
            className="btn-secondary !min-h-[2.25rem] !px-4"
            onClick={onClose}
            autoFocus
          >
            Schließen
          </button>
        </div>
        <div className="overflow-y-auto overscroll-contain px-5 py-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
          {children}
        </div>
      </div>
    </div>
  )
}
